import {useEffect, useRef, useState} from "react";
import {Mail, Phone, Clock, CheckCircle2, AlertCircle} from "lucide-react";
import useAPIService from "../../../hooks/useAPIService";

// DHW brand palette (see project requirements doc).
const DHW_BLUE = "#001489";

// Mirrors the public website's contact form (idaho-et-site/contact.html):
// same fields and the same Case Management Services Line. Submissions POST to
// /contact-message, which emails the program inbox server-side (the old
// mailto: handoff silently failed for anyone without a configured mail app).
const SUPPORT_PHONE_DISPLAY = "1-866-777-3850";
const SUPPORT_PHONE_TEL = "+18667773850";

// Limits and rules mirror contactMessageError in video-tracker-service, so the
// participant sees the problem next to the field instead of after a round
// trip. The server still re-checks everything.
const NAME_MAX = 100;
const PHONE_MAX = 30;
const MESSAGE_MAX = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidUsPhone = (p) => {
  if (!/^[\d\s().+-]+$/.test(p)) return false;
  const digits = p.replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits[0] === "1");
};

// Returns {field: message} for every invalid field; empty when sendable.
const validate = (form) => {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = "Enter your first name.";
  if (!form.lastName.trim()) errors.lastName = "Enter your last name.";
  if (!EMAIL_RE.test(form.email.trim()))
    errors.email = "Enter a valid email address, like name@example.com.";
  const phone = form.phone.trim();
  if (phone && !isValidUsPhone(phone))
    errors.phone = "Enter a 10-digit US phone number, or leave it blank.";
  if (!form.message.trim()) errors.message = "Enter your question.";
  return errors;
};

// Pre-fill from the signed-in participant. customer_id is the E.164 phone
// for phone sign-ins and the email address for email sign-ins. On a shared
// login (DOB-scoped session) the name comes from the CRM participant the DOB
// matched, not the login's own profile, so the message is sent under the
// person actually asking.
const initialForm = (user, participantName) => {
  const id = user?.customer_id || "";
  const phoneDigits = /^\+1\d{10}$/.test(id) ? id.slice(2) : "";
  const name = participantName ?? user;
  return {
    firstName: name?.firstname || "",
    lastName: name?.lastname || "",
    email: id.includes("@") ? id : "",
    phone: phoneDigits
      ? `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
      : "",
    message: "",
  };
};

const fieldClass = (error) =>
  `rounded-xl border bg-white px-4 py-3 text-base text-[#191919] focus:outline-none focus:ring-2 ${
    error
      ? "border-rose-500 focus:border-rose-600 focus:ring-rose-200"
      : "border-slate-200 focus:border-[#001489] focus:ring-[#001489]/20"
  }`;

const FieldError = ({id, error}) =>
  error ? (
    <p id={`${id}-error`} className="text-sm font-medium text-rose-700">
      {error}
    </p>
  ) : null;

const Field = ({
  id,
  label,
  type = "text",
  required,
  value,
  onChange,
  error,
  ...rest
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-semibold text-[#191919]">
      {label}{" "}
      {required ? (
        <span className="text-rose-600">*</span>
      ) : (
        <span className="font-normal text-slate-400">(optional)</span>
      )}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      className={fieldClass(error)}
      {...rest}
    />
    <FieldError id={id} error={error} />
  </div>
);

const ContactPage = ({loggedInUser, participantName = null}) => {
  const [form, setForm] = useState(() =>
    initialForm(loggedInUser, participantName),
  );
  // The CRM participant name can arrive after mount (once the DOB lookup
  // resolves). Apply it then, unless the participant already typed a name.
  const nameEdited = useRef(false);
  const participantFirst = participantName?.firstname;
  const participantLast = participantName?.lastname;
  useEffect(() => {
    if (participantName === null || nameEdited.current) return;
    setForm((f) => ({
      ...f,
      firstName: participantFirst || "",
      lastName: participantLast || "",
    }));
    // participantName itself is rebuilt on every App render; depend on the
    // values so this only runs when the name actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantFirst, participantLast]);
  const [errors, setErrors] = useState({});
  // idle -> sending -> sent | error (with a plain-language message)
  const [status, setStatus] = useState({state: "idle"});
  const {apiFetch} = useAPIService();

  const update = (e) => {
    const {name, value} = e.target;
    if (name === "firstName" || name === "lastName") nameEdited.current = true;
    setForm((f) => ({...f, [name]: value}));
    // Clear a field's error as soon as it is edited; it is re-checked on submit.
    if (errors[name]) setErrors(({[name]: _, ...rest}) => rest);
    // A stale "sent"/error notice next to edited fields reads as if it
    // applies to the new text; reset until the next submit.
    if (status.state === "sent" || status.state === "error") {
      setStatus({state: "idle"});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "sending") return;
    const found = validate(form);
    setErrors(found);
    const firstInvalid = Object.keys(found)[0];
    if (firstInvalid) {
      document.getElementById(firstInvalid)?.focus();
      return;
    }
    setStatus({state: "sending"});
    try {
      await apiFetch("/contact-message", {
        method: "POST",
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
        }),
      });
      setStatus({state: "sent"});
      // Keep the participant's details so a follow-up question starts ready.
      setForm((f) => ({...f, message: ""}));
    } catch (err) {
      setStatus({
        state: "error",
        // err.status means the server answered (validation / rate limit) with
        // a participant-readable message; anything else is a network failure.
        message: err.status
          ? err.message
          : "We could not send your message. Please check your internet " +
            "connection and try again.",
      });
    }
  };

  return (
    <div className="min-h-full bg-white pb-10 font-sans">
      <div className="mb-6">
        <p
          className="text-xs font-bold uppercase tracking-wider"
          style={{color: DHW_BLUE}}
        >
          Contact us
        </p>
        <h2 className="text-2xl font-bold text-[#191919]">
          We are here to help
        </h2>
        <p className="mt-1 max-w-2xl text-base text-slate-600">
          Call us or send a message below. If you are new and need help getting
          started, or you are not sure what to do next, our team can guide you.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr,1fr]">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className="rounded-[2rem] border border-[#ECECEC] bg-white p-5 shadow-sm md:p-7"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="firstName"
              label="First name"
              required
              value={form.firstName}
              onChange={update}
              error={errors.firstName}
              maxLength={NAME_MAX}
              autoComplete="given-name"
            />
            <Field
              id="lastName"
              label="Last name"
              required
              value={form.lastName}
              onChange={update}
              error={errors.lastName}
              maxLength={NAME_MAX}
              autoComplete="family-name"
            />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field
              id="email"
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={update}
              error={errors.email}
              autoComplete="email"
            />
            <Field
              id="phone"
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={update}
              error={errors.phone}
              maxLength={PHONE_MAX}
              autoComplete="tel"
            />
          </div>
          <div className="mt-4 flex flex-col gap-1.5">
            <label
              htmlFor="message"
              className="text-sm font-semibold text-[#191919]"
            >
              Your question <span className="text-rose-600">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              value={form.message}
              onChange={update}
              maxLength={MESSAGE_MAX}
              aria-invalid={errors.message ? true : undefined}
              aria-describedby={errors.message ? "message-error" : undefined}
              className={fieldClass(errors.message)}
            />
            <FieldError id="message" error={errors.message} />
          </div>

          <button
            type="submit"
            disabled={status.state === "sending"}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60"
            style={{backgroundColor: DHW_BLUE}}
          >
            <Mail size={18} aria-hidden="true" />
            {status.state === "sending" ? "Sending..." : "Send message"}
          </button>

          {status.state === "sent" && (
            <p
              role="status"
              className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#005B4C]"
            >
              <CheckCircle2 size={18} aria-hidden="true" />
              Your message was sent. Our team will reply by email.
            </p>
          )}
          {status.state === "error" && (
            <p
              role="alert"
              className="mt-4 flex items-start gap-2 text-sm font-semibold text-rose-700"
            >
              <AlertCircle size={18} aria-hidden="true" className="shrink-0" />
              <span>
                {status.message} You can also call us at{" "}
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="underline">
                  {SUPPORT_PHONE_DISPLAY}
                </a>
                .
              </span>
            </p>
          )}
        </form>

        {/* Info card */}
        <aside
          className="rounded-[2rem] border p-5 md:p-7"
          style={{borderColor: "#CBD3F0", backgroundColor: "#EEF1FB"}}
        >
          <h3 className="text-lg font-bold text-[#191919]">
            Case Management Services Line
          </h3>
          <a
            href={`tel:${SUPPORT_PHONE_TEL}`}
            className="mt-2 inline-flex items-center gap-2 text-2xl font-bold"
            style={{color: DHW_BLUE}}
          >
            <Phone size={20} aria-hidden="true" />
            {SUPPORT_PHONE_DISPLAY}
          </a>
          <p className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <Clock size={16} aria-hidden="true" />
            Monday&ndash;Friday, 8:00 a.m.&ndash;6:00 p.m. MT
          </p>
          <p className="mt-4 text-sm text-slate-600">
            Call this line if you are new and need help getting started, want to
            schedule an appointment, or are not sure what your next step should
            be.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default ContactPage;
